import React, { forwardRef, useCallback, type RefObject } from 'react';

import { useIntl } from 'react-intl';
import { mergeRefs } from 'use-callback-ref';

import CustomizeIcon from '@atlaskit/icon/core/customize';
import {
	ButtonMenuItem,
	type ButtonMenuItemProps,
} from '@atlaskit/side-nav-items/button-menu-item';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';
import { NavigationExperienceId } from '@atlassian/navigation-perf-utils';

import type { NavItemConfigInternal } from '../../../common/types';
import { extendWithCustomizationOptions } from '../../../common/utils/extend-with-customization-options';
import { hasCustomizableItems } from '../../../common/utils/has-customizable-items';
import { useOnSave } from '../../../common/utils/use-on-save';
import { useUserMenuDataShapes } from '../../../controllers/user-state/user-menu-data-shapes-provider';
import { customizationModalEntryPoint } from '../../customization-modal/customizationModal.entrypoint';

import { messages } from './messages';
import { useGetMenuIconAndLabel } from './use-get-menu-icon-and-label';

type CustomizeSidebarMenuItemProps = {
	appLabel: string;
	closeMenu?: () => void;
	items: Record<string, NavItemConfigInternal>;
	listItemRef?: ButtonMenuItemProps['listItemRef'];
	onClick?: () => void;
	returnFocusRef?: RefObject<HTMLElement>;
	shortcuts: Record<string, GlobalShortcutsItem>;
	testId?: string;
};

export const CustomizeSidebarMenuItem: React.ForwardRefExoticComponent<
	CustomizeSidebarMenuItemProps & React.RefAttributes<any>
> = forwardRef(CustomizeSidebarMenuItemWithRef);

function CustomizeSidebarMenuItemWithRef(
	{
		appLabel,
		closeMenu,
		items,
		onClick,
		returnFocusRef,
		listItemRef,
		shortcuts,
		testId,
	}: CustomizeSidebarMenuItemProps,
	ref: React.Ref<any>,
) {
	const getMenuIconAndLabel = useGetMenuIconAndLabel(items, shortcuts);
	const { itemUserMenuData, shortcutUserMenuData } = useUserMenuDataShapes();
	const itemExtendedUserMenuData = extendWithCustomizationOptions(itemUserMenuData, items);

	const onSave = useOnSave();
	const { formatMessage } = useIntl();
	const interactionName = NavigationExperienceId.customizationModalLoad.experienceId;

	const handleClick = useCallback(() => {
		closeMenu?.();
		onClick?.();
	}, [closeMenu, onClick]);

	if (!hasCustomizableItems(items, shortcuts)) {
		return null;
	}

	return (
		<ModalTrigger
			entryPoint={customizationModalEntryPoint}
			entryPointProps={{
				appLabel,
				getMenuIconAndLabel,
				itemExtendedUserMenuData,
				onSave,
				returnFocusRef,
				shortcutExtendedUserMenuData: shortcutUserMenuData,
			}}
		>
			{({ ref: triggerRef }) => (
				<ButtonMenuItem
					elemBefore={<CustomizeIcon label="" />}
					ref={mergeRefs([triggerRef, ref])}
					testId={testId}
					onClick={handleClick}
					interactionName={interactionName}
					listItemRef={listItemRef}
				>
					{formatMessage(messages.customizeSidebarMenuItemLabel)}
				</ButtonMenuItem>
			)}
		</ModalTrigger>
	);
}
